import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  MusicNote,
  ArrowBack,
  CheckCircle,
  Visibility,
  VisibilityOff,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { apiClient } from '../api/config';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No reset token provided. Please request a new password reset link.');
      setIsVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await apiClient.get(`/api/auth/verify-reset-token?token=${token}`);
        if (response.data.success) {
          setUserEmail(response.data.data.email);
        } else {
          setTokenError(response.data.error || 'Invalid reset token');
        }
      } catch (err: any) {
        setTokenError(err.response?.data?.error || 'This reset link is invalid or has expired.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/api/auth/reset-password', {
        token,
        password,
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.error || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Invalid token state
  if (tokenError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: { xs: '100%', sm: 400 },
            bgcolor: 'background.paper',
            borderRadius: { xs: 2, sm: 2 },
            textAlign: 'center',
          }}
        >
          <ErrorIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            Invalid Reset Link
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {tokenError}
          </Typography>
          <Button
            component={RouterLink}
            to="/forgot-password"
            variant="contained"
            sx={{ mb: 2 }}
          >
            Request New Reset Link
          </Button>
          <Box>
            <Link
              component={RouterLink}
              to="/login"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <ArrowBack fontSize="small" />
              Back to Login
            </Link>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: { xs: '100%', sm: 400 },
            bgcolor: 'background.paper',
            borderRadius: { xs: 2, sm: 2 },
            textAlign: 'center',
          }}
        >
          <CheckCircle sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            Password Reset Complete
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Your password has been successfully reset. You can now log in with your new password.
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            size="large"
          >
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // Reset form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: { xs: '100%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: { xs: 2, sm: 2 },
          boxSizing: 'border-box',
        }}
      >
        {/* Logo and Title */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
              borderRadius: 2,
              bgcolor: 'primary.main',
              mb: 2,
            }}
          >
            <MusicNote sx={{ fontSize: { xs: 32, sm: 36 }, color: 'white' }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            Reset Password
          </Typography>
          {userEmail && (
            <Typography variant="body2" color="text.secondary">
              Create a new password for <strong>{userEmail}</strong>
            </Typography>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            required
            autoComplete="new-password"
            autoFocus
            sx={{ mb: 2 }}
            helperText="Must be at least 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
            required
            autoComplete="new-password"
            sx={{ mb: 3 }}
            error={confirmPassword.length > 0 && password !== confirmPassword}
            helperText={
              confirmPassword.length > 0 && password !== confirmPassword
                ? 'Passwords do not match'
                : ''
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting || !password || !confirmPassword || password !== confirmPassword}
            sx={{ py: 1.5, mb: 2 }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Reset Password'
            )}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <ArrowBack fontSize="small" />
              Back to Login
            </Link>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
